import Spacing from 'components/Spacing';
import GridContainer from 'components/GridContainer';
import { AppSimpleSelect, FieldRow } from 'components/FormElements';
import { Controller, useFormContext } from 'react-hook-form';
import { ConsiderationTypeLabels } from 'further-ui/labels';
import {
  ConsiderationAllocationLogic,
  ConsiderationType,
} from 'further-types/lp/realisation';
import { FormControlLabel, Radio, RadioGroup, Grid } from '@mui/material';
import ProRataConsiderationForm from './ProRataConsiderationForm';
import ManualConsiderationForm from './ManualConsiderationForm';
import CurrencySelector from 'components/CurrencySelector';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { realisation as rules } from 'further-ui/validations';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import WithholdingTaxExemptTable from './WithholdingTaxExemptTable';
import AllocationsTableSection from './AllocationsTablesSection';
import useClearConsiderationForm from '../hooks/useClearConsiderationForm';
import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { RealisationFormState } from '../types';
import api from 'lib/trpcClient';
import { compact } from 'lodash';

type Props = {
  index: number;
  currency: CurrencyCode;
  selectedVehicle: Vehicle;
};

const ConsiderationEntry: React.FC<Props> = ({
  index,
  currency,
  selectedVehicle,
}) => {
  const { control, watch, getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();

  const { state, setState } = useFormWithStateContext<
    rules.CreateRealisationFormValues,
    RealisationFormState
  >();

  const disableInputs = state.consideration[index].disableAllocationsInputs;
  const showAllocationsTable = state.consideration[index].showAllocationsTable;

  const considerationAllocationLogic = watch(
    `consideration.${index}.considerationAllocationLogic`,
  );

  const holdingClassRealisations = watch('holdingClassRealisation') || [];
  const withholdingTaxList = watch('investorWithholdingTaxList') || [];

  const { data: investorAllocationsPerInvestmentData } =
    api.lpRealisation.getInvestorAllocationsPerInvestment.useQuery({
      holdingClassRealisations: getValues('holdingClassRealisation'),
      withholdingTaxList: getValues('investorWithholdingTaxList') ?? [],
      vehicleId: selectedVehicle._id,
    });

  const { clearConsiderationForm } = useClearConsiderationForm({
    vehicleCurrencyCode: selectedVehicle.baseCurrency,
  });

  const hasWithholdingTax = getValues('holdingClassRealisation').some(
    (holdingClass) =>
      holdingClass.cashConsiderationAmounts?.withholdingTaxPayable ?? 0 > 0,
  );

  const selectedCurrency = watch(`consideration.${index}.currency`) ?? currency;

  const isProRata =
    considerationAllocationLogic === ConsiderationAllocationLogic.ProRata;

  const apiUtils = api.useUtils();

  const onGenerateAllocation = async () => {
    const holdingClassInvestmentsConsiderationValues =
      await apiUtils.lpRealisation.getHoldingClassInvestmentsConsiderationValues.fetch(
        {
          holdingClassRealisations: holdingClassRealisations || [],
          withholdingTaxList: withholdingTaxList || [],
          vehicleId: selectedVehicle._id,
        },
      );

    setState({
      ...state,
      //@ts-expect-error Needs to be fixed - Realisations
      consideration: state.consideration.map((consideration, i) =>
        i === index
          ? {
              ...consideration,
              showAllocationsTable: true,
              disableAllocationsInputs: true,
              holdingClassInvestmentsConsiderationValues,
            }
          : consideration,
      ),
    });
    setValue(
      `consideration.${index}.investorAllocations`,
      compact(investorAllocationsPerInvestmentData ?? []),
    );
  };

  const onClearConsiderationForm = () => {
    clearConsiderationForm();
  };

  return (
    <GridContainer>
      <Grid size={{ md: 10, xs: 12 }}>
        <Spacing size="md">
          <FieldRow title="Enter consideration type" centerTitle>
            <Controller
              control={control}
              name={`consideration.${index}.considerationType`}
              render={({ field }) => (
                <AppSimpleSelect
                  {...field}
                  fullWidth
                  disabled={disableInputs}
                  defaultValue={ConsiderationType.CashAndWithholdingTax}
                  options={Object.entries(ConsiderationTypeLabels).map(
                    ([value, label]) => ({
                      value,
                      label,
                    }),
                  )}
                />
              )}
            />
          </FieldRow>
          <FieldRow title="Currency" centerTitle>
            <Controller
              name={`consideration.${index}.currency`}
              control={control}
              defaultValue={currency}
              render={({ field: { ref, ...rest }, fieldState: { error } }) => (
                <CurrencySelector
                  disabled={disableInputs}
                  {...rest}
                  error={error ? { message: error.message } : undefined}
                  placeholder="Select currency"
                />
              )}
            />
          </FieldRow>
          <FieldRow title="Consideration allocation rule" centerTitle>
            <Controller
              control={control}
              name={`consideration.${index}.considerationAllocationLogic`}
              render={({ field }) => {
                return (
                  <RadioGroup
                    {...field}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                  >
                    <FormControlLabel
                      value={ConsiderationAllocationLogic.ProRata}
                      control={<Radio disabled={disableInputs} />}
                      label="Enter total cash consideration and allocate pro-rata between holding classes"
                    />
                    <FormControlLabel
                      value={ConsiderationAllocationLogic.Manual}
                      control={<Radio disabled={disableInputs} />}
                      label="Manually enter cash consideration per holding class"
                    />
                  </RadioGroup>
                );
              }}
            />
          </FieldRow>
        </Spacing>
      </Grid>

      {isProRata ? (
        <Grid size={{ md: 10, xs: 12 }}>
          <Spacing size="md">
            <ProRataConsiderationForm
              control={control}
              currencyCode={selectedCurrency}
              index={index}
              disabled={disableInputs}
            />
          </Spacing>
        </Grid>
      ) : (
        <Grid size={{ md: 10, xs: 12 }}>
          <Spacing size="md">
            <ManualConsiderationForm
              currencyCode={selectedCurrency}
              disabled={disableInputs}
            />
          </Spacing>
        </Grid>
      )}
      {hasWithholdingTax && (
        <Grid container size={{ md: 10, xs: 12 }} spacing={3}>
          <Grid size={{ md: 5, xs: false }} />
          <Grid size={{ md: 7, xs: 12 }}>
            <WithholdingTaxExemptTable
              currency={selectedCurrency}
              disabled={disableInputs}
            />
          </Grid>
        </Grid>
      )}
      <Grid size={{ md: 10, xs: 12 }}>
        <ButtonGroup>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClearConsiderationForm}
          >
            Clear below and redo allocation
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onGenerateAllocation}
          >
            Generate allocation
          </Button>
        </ButtonGroup>
      </Grid>
      {showAllocationsTable &&
        state.consideration[index]
          .holdingClassInvestmentsConsiderationValues && (
          <AllocationsTableSection
            holdingClassInvestmentsConsiderationValues={
              state.consideration[index]
                .holdingClassInvestmentsConsiderationValues
            }
            currency={selectedCurrency}
            considerationIndex={index}
          />
        )}
    </GridContainer>
  );
};

export default ConsiderationEntry;
